import React, { useState, useEffect } from 'react';
import { getDesignConstant, PixelatedBubble } from '@theme/design-constants';
import type { AnimationConfig } from '@theme/animation-config';

type Props = {
  animations: {
    characters: AnimationConfig[];
    bubbles: AnimationConfig | null;
    decorations: AnimationConfig[];
  };
  isMobile: boolean;
  bubbleColor?: string;
  turtleColor?: string; // Color for turtle character animations
  enabled?: boolean; // Control visibility without unmounting
  zIndex?: number; // Custom z-index for layering
};

function AnimationLayer({ animations, isMobile, bubbleColor = '#67e8f9', turtleColor = '#10b981', enabled = true, zIndex = 9 }: Props) {
  // Fix for production hydration race condition: start hidden and fade in after mount
  const [isVisible, setIsVisible] = useState(false);

  const [bubbles, setBubbles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    size: number;
    duration: number;
  }>>([]);

  // Wait for component to mount and styles to be ready before showing animations
  useEffect(() => {
    if (!enabled) {
      // If disabled, hide immediately
      setIsVisible(false);
      return;
    }

    // Double RAF + small timeout to ensure styles are fully processed in production
    let timeoutId: NodeJS.Timeout | null = null;
    let rafId2: number | null = null;

    const rafId = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        // Small additional delay to ensure keyframes are registered
        timeoutId = setTimeout(() => {
          setIsVisible(true);
        }, 50);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (rafId2) cancelAnimationFrame(rafId2);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enabled]);

  useEffect(() => {
    if (animations.bubbles?.enabled) {
      const count = isMobile
        ? Math.floor(animations.bubbles.count / 2)
        : animations.bubbles.count;

      const newBubbles = Array.from({ length: count }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: -10 + Math.random() * 20, // Start slightly below viewport (-10% to 10%)
        size: Math.random() * (animations.bubbles!.size.max - animations.bubbles!.size.min) + animations.bubbles!.size.min,
        duration: (Math.random() * 10 + 15) / animations.bubbles!.speed
      }));
      setBubbles(newBubbles);
    }
  }, [animations.bubbles, isMobile]);

  const getAnimationStyle = (type: AnimationConfig['type'], index: number, speed: number) => {
    const baseSpeed = 15;
    const duration = baseSpeed / speed;

    switch (type) {
      case 'swimming':
        return {
          animationName: 'swim',
          animationDuration: `${duration}s`,
          animationIterationCount: 'infinite',
          animationTimingFunction: 'ease-in-out',
          animationDirection: index % 2 === 0 ? 'normal' : 'reverse',
          animationDelay: `${index * 2}s`
        };
      case 'floating':
        return {
          animationName: 'float',
          animationDuration: `${duration}s`,
          animationIterationCount: 'infinite',
          animationTimingFunction: 'ease-in-out',
          animationDelay: `${index * 1.5}s`
        };
      case 'rising':
        return {
          animationName: 'bubble',
          animationDuration: `${duration}s`,
          animationIterationCount: 'infinite',
          animationTimingFunction: 'ease-in-out',
          animationDelay: `${Math.random() * 5}s`
        };
      case 'falling':
        return {
          animationName: 'fall',
          animationDuration: `${duration / 2}s`,
          animationIterationCount: 'infinite',
          animationTimingFunction: 'ease-in',
          animationDelay: `${index * 0.5}s`
        };
      default:
        return {};
    }
  };

  const renderAnimatedElement = (config: AnimationConfig, index: number) => {
    const design = getDesignConstant(config.id);
    if (!design || !config.enabled) {
      return null;
    }

    const DesignComponent = design.component;
    const size = isMobile
      ? (config.size.min + config.size.max) / 3
      : Math.random() * (config.size.max - config.size.min) + config.size.min;

    // Special positioning logic
    let position = config.positions?.[index];

    if (!position) {
      if (config.type === 'falling') {
        // Lightning bolts - spawn at top under navbar
        position = {
          x: `${Math.random() * 90 + 5}%`,
          y: '2rem' // Adjusted to spawn higher, just under navbar
        };
      } else if (config.id === 'eagle') {
        // Eagles - spawn randomly but in upper 60% of screen (above mountains at 75%+)
        position = {
          x: `${Math.random() * 90 + 5}%`,
          y: `${Math.random() * 50 + 15}%` // 15% to 65% range (well above 75%)
        };
      } else if (config.id === 'cloud') {
        // Clouds - spawn randomly across screen
        position = {
          x: `${Math.random() * 90 + 5}%`,
          y: `${Math.random() * 80 + 10}%`
        };
      } else {
        // Default random positioning
        position = {
          x: `${Math.random() * 90 + 5}%`,
          y: `${Math.random() * 80 + 10}%`
        };
      }
    }

    // Pass color prop for turtle animations
    const componentProps: any = { size };
    if (config.id === 'turtle') {
      componentProps.color = turtleColor;
    }

    // Z-index logic for different element types
    let zIndex: number | undefined;
    if (config.id === 'sun') {
      // Sun always spawns at z-index 15 (above widgets)
      zIndex = 15;
    } else if (config.id === 'cloud') {
      // Clouds: mostly below widgets (z-index < 10), rarely above (10% chance)
      zIndex = Math.random() < 0.1 ? 15 : Math.floor(Math.random() * 5) + 1;
    } else if (config.id === 'eagle' || config.id === 'turtle') {
      // Eagles and turtles: mostly below widgets, rarely above (10% chance)
      zIndex = Math.random() < 0.1 ? 15 : Math.floor(Math.random() * 5) + 1;
    }

    return (
      <div
        key={`${config.id}-${index}`}
        className="absolute pointer-events-none"
        style={{
          left: position.x,
          top: position.y,
          opacity: 1, // Force 100% opacity for character animations
          zIndex: zIndex,
          ...getAnimationStyle(config.type, index, config.speed)
        }}
      >
        <DesignComponent {...componentProps} />
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        @keyframes swim {
          0%, 100% { transform: translateX(0) translateY(0) scaleX(1); }
          25% { transform: translateX(30px) translateY(-10px) scaleX(1); }
          50% { transform: translateX(0) translateY(-20px) scaleX(-1); }
          75% { transform: translateX(-30px) translateY(-10px) scaleX(-1); }
        }
        @keyframes bubble {
          0% { transform: translateY(0) translateX(0); opacity: 0.8; }
          100% { transform: translateY(-100vh) translateX(20px); opacity: 0; }
        }
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(calc(100vh - 80px)) rotate(180deg); opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <div
        className="fixed inset-0 pointer-events-none overflow-hidden"
        style={{
          zIndex: zIndex,
          opacity: (isVisible && enabled) ? 1 : 0,
          animationName: (isVisible && enabled) ? 'fadeIn' : 'none',
          animationDuration: (isVisible && enabled) ? '0.3s' : '0s',
          animationTimingFunction: (isVisible && enabled) ? 'ease-in' : 'linear',
          display: enabled ? 'block' : 'none' // Hide completely when disabled
        }}>
        {/* Bubbles */}
        {animations.bubbles?.enabled && bubbles.map(bubble => (
          <div
            key={bubble.id}
            className="absolute"
            style={{
              left: `${bubble.x}%`,
              bottom: `${bubble.y}%`,
              animationName: 'bubble',
              animationDuration: `${bubble.duration}s`,
              animationIterationCount: 'infinite',
              animationTimingFunction: 'ease-in-out',
              animationDelay: `${Math.random() * 5}s`
            }}
          >
            <PixelatedBubble
              size={bubble.size}
              color={bubbleColor}
              className="pixelated"
            />
          </div>
        ))}

        {/* Characters */}
        {animations.characters.map((config) => {
          return Array.from({ length: config.count }, (_, i) =>
            renderAnimatedElement(config, i)
          );
        })}

        {/* Decorations */}
        {animations.decorations.map((config) => {
          return Array.from({ length: config.count }, (_, i) =>
            renderAnimatedElement(config, i)
          );
        }
        )}
      </div>
    </>
  );
}

// Custom comparison function for React.memo to prevent unnecessary re-renders
// Only re-render if animations content, isMobile, bubbleColor, enabled, or zIndex actually change
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  // Simple props comparison
  if (prevProps.isMobile !== nextProps.isMobile ||
      prevProps.bubbleColor !== nextProps.bubbleColor ||
      prevProps.turtleColor !== nextProps.turtleColor ||
      prevProps.enabled !== nextProps.enabled ||
      prevProps.zIndex !== nextProps.zIndex) {
    return false;
  }

  // Deep comparison for animations object
  const prevAnims = prevProps.animations;
  const nextAnims = nextProps.animations;

  // Check bubbles
  if (prevAnims.bubbles?.enabled !== nextAnims.bubbles?.enabled) return false;
  if (prevAnims.bubbles?.count !== nextAnims.bubbles?.count) return false;
  if (prevAnims.bubbles?.speed !== nextAnims.bubbles?.speed) return false;

  // Check characters array
  if (prevAnims.characters.length !== nextAnims.characters.length) return false;
  for (let i = 0; i < prevAnims.characters.length; i++) {
    const prev = prevAnims.characters[i];
    const next = nextAnims.characters[i];
    if (prev.id !== next.id || prev.enabled !== next.enabled || prev.count !== next.count || prev.speed !== next.speed) {
      return false;
    }
  }

  // Check decorations array
  if (prevAnims.decorations.length !== nextAnims.decorations.length) return false;
  for (let i = 0; i < prevAnims.decorations.length; i++) {
    const prev = prevAnims.decorations[i];
    const next = nextAnims.decorations[i];
    if (prev.id !== next.id || prev.enabled !== next.enabled || prev.count !== next.count || prev.speed !== next.speed) {
      return false;
    }
  }

  // Props are equal, don't re-render
  return true;
};

export default React.memo(AnimationLayer, arePropsEqual);
